import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  Settings, 
  MessageSquare, 
  Star, 
  FileText, 
  Upload,
  Menu,
  X,
  Leaf,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: Settings, label: "Profile Settings", path: "/admin/settings" },
    { icon: MessageSquare, label: "Contact Requests", path: "/admin/contacts" },
    { icon: Star, label: "Testimonials", path: "/admin/testimonials" },
    { icon: FileText, label: "Blog Posts", path: "/admin/blog" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F4] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2D2A26] transform transition-transform duration-300 lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 text-white mb-8">
            <Leaf className="w-6 h-6 text-[#8F9E82]" />
            <span className="font-['Playfair_Display'] text-xl">Admin Panel</span>
          </Link>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  location.pathname === item.path
                    ? "bg-[#4A5D23] text-white"
                    : "text-stone-400 hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-stone-400 hover:bg-white/10 hover:text-white transition-colors"
            data-testid="admin-back-to-site"
          >
            <LogOut className="w-5 h-5" />
            <span>Back to Site</span>
          </Link>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-[#E8E4D9] p-4 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden p-2 text-[#2D2A26]"
            data-testid="admin-menu-toggle"
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <h1 className="font-['Playfair_Display'] text-xl text-[#2D2A26]">
            Benevolent Benefit Admin
          </h1>
          <div className="w-10" />
        </header>

        <div className="p-6">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="settings" element={<ProfileSettings />} />
            <Route path="contacts" element={<ContactRequests />} />
            <Route path="testimonials" element={<TestimonialsManager />} />
            <Route path="blog" element={<BlogManager />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({
    contacts: 0,
    testimonials: 0,
    blogPosts: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [contacts, testimonials, blog] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/blog`)
      ]);
      setStats({
        contacts: contacts.data.length,
        testimonials: testimonials.data.length,
        blogPosts: blog.data.length
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <div data-testid="admin-overview">
      <h2 className="font-['Playfair_Display'] text-3xl text-[#2D2A26] mb-8">Dashboard</h2>
      
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]">
          <MessageSquare className="w-8 h-8 text-[#4A5D23] mb-4" />
          <p className="text-3xl font-semibold text-[#2D2A26]">{stats.contacts}</p>
          <p className="text-[#5C5852]">Contact Requests</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]">
          <Star className="w-8 h-8 text-[#4A5D23] mb-4" />
          <p className="text-3xl font-semibold text-[#2D2A26]">{stats.testimonials}</p>
          <p className="text-[#5C5852]">Testimonials</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]">
          <FileText className="w-8 h-8 text-[#4A5D23] mb-4" />
          <p className="text-3xl font-semibold text-[#2D2A26]">{stats.blogPosts}</p>
          <p className="text-[#5C5852]">Blog Posts</p>
        </div>
      </div>
    </div>
  );
};

// Profile Settings
const ProfileSettings = () => {
  const [settings, setSettings] = useState({
    owner_name: "",
    owner_title: "",
    owner_bio: "",
    credentials: [],
    profile_image_url: ""
  });
  const [newCredential, setNewCredential] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        owner_name: settings.owner_name,
        owner_title: settings.owner_title,
        owner_bio: settings.owner_bio,
        credentials: settings.credentials
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSettings(prev => ({ ...prev, profile_image_url: response.data.image_url }));
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const addCredential = () => {
    if (newCredential.trim()) {
      setSettings(prev => ({
        ...prev,
        credentials: [...prev.credentials, newCredential.trim()]
      }));
      setNewCredential("");
    }
  };

  const removeCredential = (index) => {
    setSettings(prev => ({
      ...prev,
      credentials: prev.credentials.filter((_, i) => i !== index)
    }));
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="font-['Playfair_Display'] text-3xl text-[#2D2A26] mb-8">Profile Settings</h2>

      <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E8E4D9] max-w-2xl">
        {/* Profile Image */}
        <div className="mb-8">
          <Label className="text-[#2D2A26] font-semibold mb-4 block">Profile Image</Label>
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 rounded-2xl overflow-hidden bg-[#E8E4D9]">
              {settings.profile_image_url ? (
                <img 
                  src={`${BACKEND_URL}${settings.profile_image_url}`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#8F9E82]">
                  <Upload className="w-8 h-8" />
                </div>
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="profile-upload"
                data-testid="profile-image-input"
              />
              <label
                htmlFor="profile-upload"
                className="inline-flex items-center gap-2 bg-[#4A5D23] text-white px-4 py-2 rounded-full cursor-pointer hover:bg-[#3A4A1C] transition-colors"
              >
                {isUploading ? "Uploading..." : "Upload Image"}
              </label>
              <p className="text-xs text-[#5C5852] mt-2">JPEG, PNG, or WebP. Max 5MB.</p>
            </div>
          </div>
        </div>

        {/* Name & Title */}
        <div className="grid gap-6 mb-6">
          <div>
            <Label htmlFor="owner_name" className="text-[#2D2A26]">Your Name</Label>
            <Input
              id="owner_name"
              value={settings.owner_name}
              onChange={(e) => setSettings(prev => ({ ...prev, owner_name: e.target.value }))}
              className="mt-2"
              data-testid="input-owner-name"
            />
          </div>
          <div>
            <Label htmlFor="owner_title" className="text-[#2D2A26]">Your Title</Label>
            <Input
              id="owner_title"
              value={settings.owner_title}
              onChange={(e) => setSettings(prev => ({ ...prev, owner_title: e.target.value }))}
              className="mt-2"
              placeholder="e.g., End of Life Doula & Companion"
              data-testid="input-owner-title"
            />
          </div>
          <div>
            <Label htmlFor="owner_bio" className="text-[#2D2A26]">Bio</Label>
            <Textarea
              id="owner_bio"
              value={settings.owner_bio}
              onChange={(e) => setSettings(prev => ({ ...prev, owner_bio: e.target.value }))}
              className="mt-2"
              rows={4}
              placeholder="Tell your story..."
              data-testid="input-owner-bio"
            />
          </div>
        </div>

        {/* Credentials */}
        <div className="mb-6">
          <Label className="text-[#2D2A26] font-semibold mb-4 block">Credentials & Certifications</Label>
          <div className="flex gap-2 mb-4">
            <Input
              value={newCredential}
              onChange={(e) => setNewCredential(e.target.value)}
              placeholder="Add a credential..."
              onKeyPress={(e) => e.key === "Enter" && addCredential()}
              data-testid="input-new-credential"
            />
            <Button onClick={addCredential} className="bg-[#4A5D23]" data-testid="add-credential-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.credentials.map((cred, index) => (
              <span 
                key={index}
                className="inline-flex items-center gap-2 bg-[#E8E4D9] px-3 py-1 rounded-full text-sm"
              >
                {cred}
                <button onClick={() => removeCredential(index)} className="text-[#5C5852] hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <Button onClick={handleSave} className="bg-[#4A5D23] w-full" data-testid="save-settings-btn">
          Save Settings
        </Button>
      </div>
    </div>
  );
};

// Contact Requests
const ContactRequests = () => {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      toast.success("Contact deleted");
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  return (
    <div data-testid="admin-contacts">
      <h2 className="font-['Playfair_Display'] text-3xl text-[#2D2A26] mb-8">Contact Requests</h2>

      {contacts.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#E8E4D9]">
          <MessageSquare className="w-12 h-12 text-[#8F9E82] mx-auto mb-4" />
          <p className="text-[#5C5852]">No contact requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.id} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-[#2D2A26]">{contact.name}</h3>
                  <p className="text-sm text-[#5C5852]">{contact.email} • {contact.phone}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Contact?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteContact(contact.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              {contact.service_type && (
                <span className="inline-block bg-[#4A5D23]/10 text-[#4A5D23] px-3 py-1 rounded-full text-xs mb-3">
                  {contact.service_type}
                </span>
              )}
              <p className="text-[#5C5852]">{contact.message}</p>
              <p className="text-xs text-[#8F9E82] mt-4">
                {new Date(contact.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Testimonials Manager
const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    relationship: "",
    content: "",
    rating: 5
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, formData);
        toast.success("Testimonial updated!");
      } else {
        await axios.post(`${API}/testimonials`, formData);
        toast.success("Testimonial added!");
      }
      fetchTestimonials();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to save testimonial");
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      setTestimonials(testimonials.filter(t => t.id !== id));
      toast.success("Testimonial deleted");
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", relationship: "", content: "", rating: 5 });
    setEditingTestimonial(null);
  };

  const openEditDialog = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      relationship: testimonial.relationship,
      content: testimonial.content,
      rating: testimonial.rating
    });
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-['Playfair_Display'] text-3xl text-[#2D2A26]">Testimonials</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A5D23]" data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTestimonial ? "Edit" : "Add"} Testimonial</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="testimonial-name"
                />
              </div>
              <div>
                <Label>Relationship</Label>
                <Input
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  placeholder="e.g., Daughter of client"
                  data-testid="testimonial-relationship"
                />
              </div>
              <div>
                <Label>Testimonial</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={4}
                  data-testid="testimonial-content"
                />
              </div>
              <div>
                <Label>Rating (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                  data-testid="testimonial-rating"
                />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-[#4A5D23]" data-testid="save-testimonial-btn">
                {editingTestimonial ? "Update" : "Add"} Testimonial
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {testimonials.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#E8E4D9]">
          <Star className="w-12 h-12 text-[#8F9E82] mx-auto mb-4" />
          <p className="text-[#5C5852]">No testimonials yet. Add your first one!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-[#2D2A26]">{testimonial.name}</h3>
                  <p className="text-sm text-[#8F9E82]">{testimonial.relationship}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(testimonial)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Testimonial?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteTestimonial(testimonial.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <p className="text-[#5C5852] text-sm line-clamp-3">"{testimonial.content}"</p>
              <div className="flex items-center gap-1 mt-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#4A5D23] text-[#4A5D23]" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Blog Manager
const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    image_url: "",
    is_published: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData);
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/blog`, formData);
        toast.success("Post created!");
      }
      fetchPosts();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const deletePost = async (id) => {
    try {
      await axios.delete(`${API}/blog/${id}`);
      setPosts(posts.filter(p => p.id !== id));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, {
        ...post,
        is_published: !post.is_published
      });
      fetchPosts();
      toast.success(post.is_published ? "Post unpublished" : "Post published");
    } catch (error) {
      toast.error("Failed to update post");
    }
  };

  const resetForm = () => {
    setFormData({ title: "", excerpt: "", content: "", image_url: "", is_published: true });
    setEditingPost(null);
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url || "",
      is_published: post.is_published
    });
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-['Playfair_Display'] text-3xl text-[#2D2A26]">Blog Posts</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A5D23]" data-testid="add-blog-btn">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? "Edit" : "Create"} Blog Post</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="blog-title"
                />
              </div>
              <div>
                <Label>Excerpt</Label>
                <Textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  rows={2}
                  placeholder="Brief summary..."
                  data-testid="blog-excerpt"
                />
              </div>
              <div>
                <Label>Content</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  placeholder="Full article content..."
                  data-testid="blog-content"
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="blog-image-url"
                />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-[#4A5D23]" data-testid="save-blog-btn">
                {editingPost ? "Update" : "Create"} Post
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {posts.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#E8E4D9]">
          <FileText className="w-12 h-12 text-[#8F9E82] mx-auto mb-4" />
          <p className="text-[#5C5852]">No blog posts yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9] flex gap-6">
              {post.image_url && (
                <img src={post.image_url} alt={post.title} className="w-32 h-24 object-cover rounded-xl" />
              )}
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-[#2D2A26]">{post.title}</h3>
                    <p className="text-sm text-[#5C5852] line-clamp-2 mt-1">{post.excerpt}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Button variant="ghost" size="sm" onClick={() => togglePublish(post)}>
                      {post.is_published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(post)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Post?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deletePost(post.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-[#8F9E82]">
                  <span>{post.is_published ? "Published" : "Draft"}</span>
                  <span>•</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
